import React, { useEffect } from 'react';
import { RouterProvider, useStateContext, UiModel, useSnackbar } from "@durlabh/dframework-ui";
import { BrowserRouter as Router, useLocation, useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Dashboard from './components/dashboard';
import EquipmentFamily from './components/EquipmentFamily';
import Chemistry from './components/Chemistry';
import Detergent from './components/Detergent';
import ManufactureModel from './components/Manufacture';
import RegionModel from './components/RegionModel';
import UserModel from './components/UserModel';
import Layout from './components/Layout';
import ValidateScope from './components/grid/ValidateScope';
import Login from './components/Login';
import coreConfig from './common/coreConfig';
import ScopeType from './components/ScopeType';
import Country from './components/Country';
import LookupType from './components/LookupTypeModel';
import LookupScreen from './components/LookupScreen';
import LookupForm from './components/LookupForm';
import ScopeDetails from './components/ScopeDetails';
import ScopeSearchGrid from './components/ScopeSearchGrid';
import MasterScopeScreen from './components/MasterScopeScreen';
import ScopeScreen from './components/scopescreen';
import HistoryModel from './components/HistoryModel';
import Role from './components/role';
import RoleModuleForm from './components/role/RoleModuleForm';
import RoleModuleScreen from './components/role/RoleModuleScreen';
import documentConfig from './components/childGrids/Document';
import HistoryModuleScreen from './components/HistoryGrid';
import AddDocumentForm from './components/AddDocumentForm';
import axiosBase from './common/axiosBase';
import ReleaseManagementGrid from './components/grid/releaseManagement';
import ReleaseScreen from './components/ReleaseScreen';
import { isAuthenticated } from './common/auth';
import ArchiveScreen from './components/ArchiveScreen';
import HookupConfigForm from './components/HookupConfigForm';
import ParameterSetForm from './components/ParametreSetsForm';
import JsError from './JsError';
import AddHookupDocumentGrid from './components/AddHookupDocumentGrid';
import ReleaseManagementForm from './components/ReleaseManagementForm';
import { setSnackbar } from './common/snackbar';

const JsErrorClass = new JsError();

const models = [
  documentConfig
];

const DocumentModel = new UiModel({ ...documentConfig, permissions: { add: false, edit: true, delete: true } });

function InnerComponent({ documentGroupMap }) {
  const [userData, setUserData] = React.useState({ userDetails: null });
  const location = useLocation();
  const navigate = useNavigate();
  const snac = useSnackbar();
  const contextValue = {
    pathname: location.pathname,
    navigate: navigate,
    useParams
  };
  const isAuthenticatedUser = async () => {
    const query = new URLSearchParams(location.search);
    const isLogout = !!query.get("isLogout");
    if (isLogout) {
      return;
    }
    const data = await isAuthenticated();
    if (data?.userDetails) {
      const { permissions, userDetails, menuDetails } = data;
      setUserData({ userDetails, permissions, menuDetails });
    } else {
      if (data?.message && !data.message.toLowerCase().includes("session")) {
        snac.showError(data.message);
      }
      setUserData({ userDetails: null });
      navigate('/login');
    }
  };

  useEffect(() => {
    isAuthenticatedUser();
  }, []);

  useEffect(() => {
    if (userData?.IsLoggedIn) {
      navigate('/dashboard');
    }
  }, [userData]);

  React.useEffect(() => {
    setSnackbar(snac);
  }, [snac]);
  
  return (
    <RouterProvider value={contextValue}>
      <Routes>
        <Route path="/login" element={<Login userData={userData} setUserData={setUserData} />} />
        {userData.userDetails && <Route path="/" element={<Layout userData={userData} />} >
          <Route path="/dashboard" element={<Dashboard userData={userData} />} />
          <Route path="/validatescope/:id" element={<ValidateScope />} />
          <Route path="/validatescope/:equipmentId/:id" element={<ScopeDetails />} />
          <Route path="/validatescope/:equipmentId/:equipmentScopeId/hookup/:id" element={<HookupConfigForm />} />
          <Route path="/validatescope/:equipmentId/:equipmentScopeId/parameterset/:id" element={<ParameterSetForm />} />
          <Route path="/users" element={<UserModel.Grid />} />
          <Route path="/users/:id" element={<UserModel.Form />} />
          <Route path="/chemistry" element={<Chemistry.Grid />} />
          <Route path="/chemistry/:id" element={<Chemistry.Form />} />
          <Route path="/detergent" element={<Detergent.Grid />} />
          <Route path="/detergent/:id" element={<Detergent.Form />} />
          <Route path="/lookups" element={<LookupType.Grid />} />
          <Route path="/lookups/:id" element={<LookupScreen />} />
          <Route path="/lookups/:lookupTypeId/:id" element={<LookupForm />} />
          <Route path="/manufacturer" element={<ManufactureModel.Grid />} />
          <Route path="/manufacturer/:id" element={<ManufactureModel.Form />} />
          <Route path='/region' element={<RegionModel.Grid />} />
          <Route path="/region/:id" element={<RegionModel.Form />} />
          <Route path="/scopetype" element={<ScopeType.Grid />} />
          <Route path='/scopetype/:id' element={<ScopeType.Form />} />
          <Route path="/releasemgmt" element={<ReleaseManagementGrid />} />
          <Route path="/countries" element={<Country.Grid />} />
          <Route path="/countries/:id" element={<Country.Form />} />
          <Route path="/equipments" element={<EquipmentFamily.Grid />} />
          <Route path="/equipments/:id" element={<EquipmentFamily.Form models={models} relationFilters={{
            "Document": [{
              field: "DocumentGroupId",
              operator: "is",
              type: "Number",
              value: documentGroupMap?.Equipment
            }]
          }} />} />
          <Route path='/searchScope/:equipmentid/:revisionId' element={<ScopeSearchGrid />} />
          <Route path='/masterscope' element={<MasterScopeScreen.Grid />} />
          <Route path='/masterscope/:id' element={<MasterScopeScreen.Form models={models} relationFilters={{
            "Document": [{
              field: "DocumentGroupId",
              operator: "is",
              type: "Number",
              value: documentGroupMap?.Scope
            }]
          }} />} />
          <Route path="/scopescreen" element={<ScopeScreen />} />
          <Route path='/history' element={<HistoryModel.Grid />} />
          <Route path='/roles' element={<Role.Grid />} />
          <Route path="/roles/:id" element={<RoleModuleScreen />} />
          <Route path="/roles/:roleModuleId/:id" element={<RoleModuleForm />} />
          <Route path="/historyScreen" element={<HistoryModuleScreen />} />
          <Route path="/release/:id" element={<ReleaseScreen />} />
          <Route path="/document" element={<DocumentModel.Grid />} />
          <Route path='/document/:id' element={<DocumentModel.Form />} />
          <Route path='/:documentGroup/:associationId/:id' element={<AddDocumentForm documentGroupMap={documentGroupMap} />} />
          <Route path='/archive/:revisionId' element={<ArchiveScreen />} />
          <Route path="/revision/:id" element={<ReleaseManagementForm models={models} relationFilters={{
            "Document": [{
              field: "DocumentGroupId",
              operator: "is",
              type: "Number",
              value: documentGroupMap?.Revision
            }]
          }} />} />
          <Route path="/validatescope/:equipmentId/:equipmentScopeId/document/:id" element={<AddHookupDocumentGrid equipmentGroupId={documentGroupMap?.Equipment} />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>}
      </Routes>
    </RouterProvider>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2'
    },
    secondary: {
      main: '#dc004e'
    },
    action: {
      disabled: '#dfdede'
    }
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        filterForm: {
          width: 500
        }
      }
    }
  }
});



function App() {

  const { dispatchData } = useStateContext();
  const [documentGroupMap, setDocumentGroupMap] = React.useState(null);
  const snac = useSnackbar();

  const fetchDocumentGroupMap = async () => {
    const { apiConfig } = coreConfig;
    try {
      const response = await axiosBase({
        api: apiConfig.endpoints.documentGroupLookup,
        method: apiConfig.reqMethod.GET
      });
      const docGroups = response.data.lookups.DocumentGroup || [];
      setDocumentGroupMap(docGroups.reduce((acc, item) => {
        acc[item.label] = item.value;
        return acc;
      }, {}));
    } catch (error) {
      snac.showError(error?.message || 'Failed to fetch document group map');
    }
  };
  useEffect(() => {
    fetchDocumentGroupMap();
    dispatchData({ type: 'UPDATE_DATE_TIME', payload: 'MM/DD/YYYY hh:mm:ss A' });
    dispatchData({ type: 'SET_GRID_SETTINGS', payload: { permissions: { edit: true, add: true, export: true, delete: true, Url: `${coreConfig.backendUrl}/api/`, preferenceApi: `${coreConfig.backendUrl}/preferences`, uploadApi: `${coreConfig.backendUrl}/upload`, mediaApi: `${coreConfig.backendUrl}/media` } } });

    JsErrorClass.init();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <InnerComponent documentGroupMap={documentGroupMap} dispatchData={dispatchData} />
      </Router>
    </ThemeProvider>
  );
}

const logger = JsErrorClass.logError;
export { logger };
export default App;

